import { ModelCreateDto } from '@vatos-pas/common';

import { ModelFormValues } from './components/ModelForm';
import { CostValue, EMPTY_COST } from './components/ModelFormFields';

type ClearableKeys =
  | 'stockingSpec'
  | 'defaultTotalSquareFootage'
  | 'defaultInteriorOptionCost'
  | 'defaultExteriorOptionCost'
  | 'defaultTrimOptionCost'
  | 'defaultExteriorSidingPaintAmount';

// The DTO types the optional fields as `?: T`, but their API columns are nullable
// and a value the user cleared must be sent as null to overwrite the stored one.
export type ModelPayload = Omit<ModelCreateDto, ClearableKeys> & {
  stockingSpec: string | null;
  defaultTotalSquareFootage: number | null;
  defaultInteriorOptionCost: number | null;
  defaultExteriorOptionCost: number | null;
  defaultTrimOptionCost: number | null;
  defaultExteriorSidingPaintAmount: number | null;
};

const numberOrNull = (value: CostValue | string): number | null =>
  value === EMPTY_COST ? null : Number(value);

// A cost only means something while its toggle is on; a stale stored cost must
// not survive a save made with the toggle off.
const gatedCost = (enabled: boolean, value: CostValue): number | null =>
  enabled ? numberOrNull(value) : null;

/** Create and update send the same shape, so both views share this mapping. */
export const toModelPayload = (values: ModelFormValues): ModelPayload => ({
  name: values.name,
  subdivisionId: values.subdivisionId,
  externalId: values.externalId,
  description: values.description,
  stockingSpec: values.stockingSpec || null,
  active: values.active,
  garageFirst: values.garageFirst,
  hasTrim: values.hasTrim,
  hasExteriorPaint: values.hasExteriorPaint,
  defaultExteriorSidingPaintAmount: gatedCost(
    values.hasExteriorPaint,
    values.defaultExteriorSidingPaintAmount,
  ),
  highCeiling: values.highCeiling,
  screwInspection: values.screwInspection,
  hasInteriorOptions: values.hasInteriorOptions,
  defaultInteriorOptionCost: gatedCost(
    values.hasInteriorOptions,
    values.defaultInteriorOptionCost,
  ),
  hasExteriorOptions: values.hasExteriorOptions,
  defaultExteriorOptionCost: gatedCost(
    values.hasExteriorOptions,
    values.defaultExteriorOptionCost,
  ),
  hasTrimOptions: values.hasTrimOptions,
  defaultTrimOptionCost: gatedCost(
    values.hasTrimOptions,
    values.defaultTrimOptionCost,
  ),
  defaultTotalSquareFootage: numberOrNull(values.defaultTotalSquareFootage),
});
